import React from 'react';

export const TEAL = '#00D4FF';
export const PINK = '#FF4D94';
export const PURPLE = '#A78BFA';
export const GREEN = '#4ade80';
export const AMBER = '#FFC83D';
export const BG = '#0A0A0A';
export const CARD = '#141414';
export const INK = '#0F0F0F';

export function Kicker({ children, color = TEAL, className = '' }) {
  return (
    <p className={`text-[10px] uppercase tracking-[0.28em] font-black ${className}`} style={{ color }}>
      {children}
    </p>
  );
}

export function Section({ kicker, title, accent = TEAL, children, testid, delay = 0 }) {
  return (
    <section
      className="ov-reveal rounded-xl p-6 lg:p-8 relative overflow-hidden"
      style={{
        background: CARD,
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: `0 0 60px ${accent}10`,
        animationDelay: `${delay}ms`,
      }}
      data-testid={testid}
    >
      <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
           style={{ background: accent }} />
      <div className="relative">
        {kicker && <Kicker color={accent} className="mb-2">{kicker}</Kicker>}
        {title && (
          <h2 className="text-2xl lg:text-3xl font-black text-white tracking-tight leading-tight mb-6 max-w-3xl">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

const STATUS = {
  built:      { label: 'Built',      color: GREEN },
  developing: { label: 'Developing', color: AMBER },
  accruing:   { label: 'Built · accruing', color: TEAL },
  filed:      { label: 'Provisional filed', color: PURPLE },
};

export function StatusChip({ status, testid }) {
  const s = STATUS[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-[0.18em]"
      style={{ color: s.color, background: `${s.color}14`, border: `1px solid ${s.color}44` }}
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

export function StatLine({ label, value, note, accent }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">
        {label}
        {note && <span className="block normal-case tracking-normal text-[10px] text-gray-600 font-medium mt-0.5">{note}</span>}
      </span>
      <span className="text-sm font-bold tabular-nums text-right" style={{ color: accent || '#fff' }}>{value}</span>
    </div>
  );
}

export function Callout({ accent = TEAL, label, children, testid }) {
  return (
    <div className="rounded-lg p-4 lg:p-5" style={{ background: `${accent}08`, border: `1px solid ${accent}33` }} data-testid={testid}>
      {label && <Kicker color={accent} className="mb-1.5 text-[11px] tracking-[0.22em]">{label}</Kicker>}
      <div className="text-base text-white leading-snug">{children}</div>
    </div>
  );
}

export function Tile({ children, className = '', style = {}, testid }) {
  return (
    <div className={`rounded-lg p-4 bg-black/40 border border-white/5 ${className}`} style={style} data-testid={testid}>
      {children}
    </div>
  );
}

export function WaveLogoMark({ size = 26, animated = false }) {
  const id = `wlm-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={TEAL} />
          <stop offset="100%" stopColor={PINK} />
        </linearGradient>
      </defs>
      <path d="M8 38 C 16 28, 24 28, 32 38 S 48 48, 56 38" stroke={`url(#${id})`} strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M8 28 C 16 18, 24 18, 32 28 S 48 38, 56 28" stroke={`url(#${id})`} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.65" />
      <path d="M8 18 C 16 12, 24 12, 32 18 S 48 24, 56 18" stroke={`url(#${id})`} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4">
        {animated && <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3s" repeatCount="indefinite" />}
      </path>
    </svg>
  );
}
